import { LOCATION_REQUIRED_STATUS } from "@/src/features/locations/placeSearchAnchor";

/**
 * A finished job that stopped to ask the person for something before it can
 * do what was asked. Its completion is a question, not a task result: the
 * feed holds it as needing an answer (`buildRowPresentation`).
 *
 * It is read only from what the job's functions returned in structured form
 * (a result `status` and its typed fields), never from the answer's words:
 * a place search that has no anchor returns `location_required` with the
 * reason (`specialized_external_search`, `placeSearchAnchor`).
 *
 * - `need`: what the person has to give. `location` is their position or an
 *   area; `home_address` / `work_address` is a saved place they have not
 *   stored yet.
 * - `subject`: what the job wanted it for, from the function's own arguments
 *   (search kind and query), normalized for comparison only. Two pending
 *   questions with the same need and subject are one question
 *   (`completionAskKey`). Null when the function gave none.
 */
export type CompletionAwaiting = { need: CompletionAwaitingNeed; subject: string | null };
export type CompletionAwaitingNeed = "location" | "home_address" | "work_address";

const NEEDS: ReadonlySet<string> = new Set<CompletionAwaitingNeed>(["location", "home_address", "work_address"]);
/** A stored subject is at most this long; a longer one is cut, so the key stays bounded. */
export const COMPLETION_AWAITING_SUBJECT_MAX = 240;

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/** The `fetchedData` of each item of a job result (a list of `{ fetchedData }`, or one). */
export function jobResultFetchedData(result: unknown): Record<string, unknown>[] {
  const items = Array.isArray(result) ? result : [result];
  return items.flatMap((item) => {
    const fetchedData = record(record(item)?.fetchedData);
    return fetchedData ? [fetchedData] : [];
  });
}

/**
 * Text as a comparison key: Unicode NFKC, lower case, runs of whitespace as
 * one space, trimmed and bounded. It changes no meaning: "Coffee  shops" and
 * "coffee shops" compare equal, "coffee" and "coffee shops" do not.
 */
function comparable(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim().slice(0, COMPLETION_AWAITING_SUBJECT_MAX);
}

function needOf(locationRequired: Record<string, unknown> | null): CompletionAwaitingNeed {
  const place = locationRequired?.place;
  // A saved place the user never stored is asked for as that address; every
  // other reason (no position, a self reference, an unreadable saved place)
  // asks for a position or an area.
  if (locationRequired?.reason === "saved_place_unknown" && (place === "home" || place === "work")) return `${place}_address`;
  return "location";
}

/**
 * What a job's result waits on the person for, or null when it waits on
 * nothing. One question per job: when several of its searches asked, the
 * first need wins and the subject lists every asking search, sorted, so the
 * same set reads the same in any order.
 */
export function completionAwaitingOfResult(result: unknown): CompletionAwaiting | null {
  const asks = jobResultFetchedData(result).filter((fetchedData) => fetchedData.status === LOCATION_REQUIRED_STATUS);
  if (asks.length === 0) return null;
  const need = needOf(record(asks[0].locationRequired));
  const subjects = Array.from(new Set(asks.flatMap((fetchedData) => {
    const query = comparable(fetchedData.query);
    return query ? [`${comparable(fetchedData.kind)}:${query}`] : [];
  }))).sort();
  const subject = subjects.join(";").slice(0, COMPLETION_AWAITING_SUBJECT_MAX);
  return { need, subject: subject || null };
}

/** A stored `jobOutcome.awaiting`, validated, or null. */
export function parseCompletionAwaiting(value: unknown): CompletionAwaiting | null {
  const stored = record(value);
  if (!stored || typeof stored.need !== "string" || !NEEDS.has(stored.need)) return null;
  const subject = typeof stored.subject === "string" && stored.subject.length > 0 && stored.subject.length <= COMPLETION_AWAITING_SUBJECT_MAX
    ? stored.subject
    : null;
  return { need: stored.need as CompletionAwaitingNeed, subject };
}

/**
 * The key under which pending questions fold into one row: same need and same
 * subject. Null without a subject: a question whose purpose is unknown never
 * folds with another, since folding would hide a different request.
 */
export function completionAskKey(awaiting: CompletionAwaiting): string | null {
  return awaiting.subject === null ? null : `ask:${awaiting.need}|${awaiting.subject}`;
}
